import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import {
  isMobile,
  isIos,
  EnabledVitalSigns,
  HealthMonitorCodes,
  SessionState,
} from "@biosensesignal/web-sdk";
import { useMediaPredicate } from "react-media-hook";
import { useIntl } from "react-intl";
import {
  useError,
  useLicenseKey,
  useMeasurementDuration,
  useMonitor,
  usePageVisibility,
  usePrevious,
  useWarning,
  useResultsExport,
} from "../hooks";
import { resolveScanDuration } from "../hooks/useLicenseDetails";
import { BrandingContext } from "./shared/BrandingContext";
import { consumeVoucherCredit } from "../api/voucherCredits";
import StartButton from "./StartButton";
import { mirror } from "../style/mirror";
import { Flex } from "./shared/Flex";
import Timer from "./Timer";
import OrbitDot, {
  OVAL_CX,
  OVAL_CY,
  OVAL_RX,
  OVAL_RY,
  RING_OUTLINE_OPACITY,
  RING_OUTLINE_WIDTH,
  RING_WHITE,
} from "./OrbitDot";
import HeartbeatTrace from "./HeartbeatTrace";
import ScanLight from "./ScanLight";
import FaceColorPass from "./FaceColorPass";
import media from "../style/media";
import InfoBar from "./InfoBar";
import { ErrorAlert, InfoAlert, WarningAlert } from "./alert";
import Loader from "./Loader";
import { VideoReadyState } from "../types";
import TopBar from "./TopBar";
import FooterLogo from "./FooterLogo";
import { MeasurementResults } from "../hooks/useResultsExport";
import {
  buildBackendPayload,
  mapDemographics,
  ScanAlertCodes,
  ScanDataPayload,
  SessionResult,
  withCeClinicalFields,
} from "../utils/buildBackendPayload";
import { postScanData, postScanDataBeacon } from "../api/scanData";
import { fontSize, fontWeight, buttonDefaults, colors, radius } from "../style/tokens";

/**
 * Outer wrapper that blocks ALL pointer events on the entire monitor tree
 * (TopBar, MonitorWrapper) while the
 * settings panel is open or briefly after it closes (to absorb ghost clicks
 * from touch devices that fire a synthetic click ~300ms after touchend).
 */
const MonitorOuterWrapper = styled.div<{ isBlocked: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  pointer-events: ${({ isBlocked }) => (isBlocked ? "none" : "auto")};
`;

const MonitorWrapper = styled(Flex)<{ isSettingsOpen: boolean }>`
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  z-index: ${({ isSettingsOpen }) => isSettingsOpen && "-1"};
`;

const MeasurementContentWrapper = styled(Flex)`
  width: 100%;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  /* Anchor for OvalControls (the start/retry button + alerts overlay). Placed
     here rather than on ScanArea so ScanArea's overflow:hidden can't clip the
     button once alerts push it below the oval. */
  position: relative;
`;

const ScanArea = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  min-height: 0;
  isolation: isolate;
  background: #111;
  overflow: hidden;
`;

const VideoWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`;

/* keep VideoAndStatsWrapper as alias so existing countdown/alert children compile */
const VideoAndStatsWrapper = ScanArea;

type StatusColor = "none" | "error" | "warning" | "success";

const getMaskFillColor = (status: StatusColor): string => {
  switch (status) {
    case "error":
      return "rgba(255, 68, 68, 0.5)"; // Red tint
    case "warning":
      return "rgba(255, 170, 0, 0.5)"; // Orange/amber tint
    case "success":
      return "rgba(255, 255, 255, 0.5)"; // White tint
    default:
      return "rgba(24, 25, 30, 0.4)"; // Original dark color
  }
};

const MaskSvgWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;

  svg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/* The ScanLight face-scan sweep is hidden for now; flip to true to bring it
   back (the ring countdown and HeartbeatTrace don't depend on it). */
const SHOW_SCAN_LIGHT = false;

/* Layer between the camera image and the mask whose contents are blended onto
   the video (the mask's own z-index isolates it, so it can't blend). Holds the
   ScanLight sweep, which brightens the skin it passes over. z-index 1
   lifts it above the video, which the mirror transform paints as a layer after
   this one; the mask, also z-index 1 but later in the DOM, stays on top. */
const ReflectionLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  mix-blend-mode: overlay;

  svg {
    width: 100%;
    height: 100%;
  }
`;

interface MaskProps {
  status: StatusColor;
  /* Brand colour for the scan animations (the outline and resting markers are white). */
  color: string;
  /* Scan length: one oval marker per second. */
  durationSeconds: number;
  /* True while a scan is running: starts the orbit, ScanLight (if shown) and HeartbeatTrace. */
  scanning: boolean;
  /* Live heart rate for the HeartbeatTrace; absent until the SDK reports one. */
  bpm?: number | null;
  /* The camera image, for FaceColorPass. */
  video: React.RefObject<HTMLVideoElement | null>;
  /* Partner branding switches: the progress ring (OrbitDot) and face sweep
     (FaceColorPass), and the heart rate monitor (HeartbeatTrace). */
  animation: boolean;
  heartMonitor: boolean;
}

const DynamicMask: React.FC<MaskProps> = ({
  status,
  color,
  durationSeconds,
  scanning,
  bpm,
  video,
  animation,
  heartMonitor,
}) => {
  const fillColor = getMaskFillColor(status);
  const [reflectionSvg, setReflectionSvg] = useState<SVGSVGElement | null>(null);

  return (
    <>
      {SHOW_SCAN_LIGHT && (
        <ReflectionLayer>
          <svg
            ref={setReflectionSvg}
            viewBox="0 0 820 615"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          />
        </ReflectionLayer>
      )}
      {SHOW_SCAN_LIGHT && scanning && (
        <ScanLight color={RING_WHITE} container={reflectionSvg} />
      )}
      {animation && scanning && <FaceColorPass video={video} />}
      <MaskSvgWrapper>
        <svg
          width="820"
          height="615"
          viewBox="0 0 820 615"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M820 0H0V615H820V0ZM410.5 482C495.276 482 564 391.785 564 280.5C564 169.215 495.276 79 410.5 79C325.724 79 257 169.215 257 280.5C257 391.785 325.724 482 410.5 482Z"
            fill={fillColor}
            style={{ transition: "fill 0.3s ease" }}
          />
          <g opacity="0.5">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M409.328 87.5929V85.1433H411.672V87.5929H409.328ZM409.328 97.3915V92.4922H411.672V97.3915H409.328ZM409.328 107.19V102.291L411.672 102.291V107.19H409.328ZM409.328 116.989V112.089H411.672V116.989H409.328ZM409.328 126.787V121.888H411.672V126.787H409.328ZM409.328 136.586V131.686H411.672V136.586H409.328ZM409.328 146.384V141.485H411.672V146.384H409.328ZM409.328 156.183V151.284H411.672V156.183H409.328ZM409.328 165.981V161.082H411.672V165.981H409.328ZM409.328 175.78V170.881H411.672V175.78H409.328ZM409.328 185.578V180.679H411.672V185.578H409.328ZM409.328 195.377V190.478H411.672V195.377H409.328ZM409.328 205.176V200.276H411.672V205.176H409.328ZM409.328 214.974V210.075H411.672V214.974H409.328ZM409.328 224.773V219.873H411.672V224.773H409.328ZM409.328 234.571V229.672H411.672V234.571H409.328ZM409.328 244.37V239.471H411.672V244.37H409.328ZM409.328 254.168V249.269H411.672V254.168H409.328ZM409.328 263.967V259.068H411.672V263.967H409.328ZM409.328 273.766V268.866H411.672V273.766H409.328ZM409.328 283.564V278.665H411.672V283.564H409.328ZM409.328 293.363V288.463H411.672V293.363H409.328ZM409.328 303.161L409.328 298.262H411.672L411.672 303.161H409.328ZM409.328 312.96V308.06H411.672V312.96H409.328ZM409.328 322.758V317.859H411.672V322.758H409.328ZM409.328 332.557V327.658H411.672V332.557H409.328ZM409.328 342.355V337.456H411.672V342.355H409.328ZM409.328 352.154V347.255H411.672V352.154H409.328ZM409.328 361.953V357.053H411.672V361.953H409.328ZM409.328 371.751V366.852H411.672V371.751H409.328ZM409.328 381.55V376.65H411.672V381.55H409.328ZM409.328 391.348V386.449H411.672V391.348H409.328ZM409.328 401.147V396.248H411.672V401.147H409.328ZM409.328 410.945V406.046H411.672V410.945H409.328ZM409.328 420.744V415.845H411.672V420.744H409.328ZM409.328 430.542V425.643H411.672V430.542H409.328ZM409.328 440.341V435.442H411.672V440.341H409.328ZM409.328 450.139V445.24H411.672V450.139H409.328ZM409.328 459.938V455.039H411.672V459.938H409.328ZM409.328 469.736V464.837H411.672V469.736H409.328ZM409.328 477.085V474.636H411.672V477.085H409.328Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M556.997 269.442H559.313V271.899H556.997V269.442ZM547.733 269.442H552.365V271.899H547.733V269.442ZM538.469 269.442H543.101V271.899H538.469V269.442ZM529.204 269.442H533.836V271.899H529.204V269.442ZM519.94 269.442H524.572V271.899H519.94V269.442ZM510.676 269.442H515.308V271.899H510.676V269.442ZM501.412 269.442H506.044V271.899H501.412V269.442ZM492.148 269.442H496.78V271.899H492.148V269.442ZM482.883 269.442H487.515V271.899H482.883V269.442ZM473.619 269.442H478.251V271.899H473.619V269.442ZM464.355 269.442H468.987V271.899H464.355V269.442ZM455.091 269.442H459.723V271.899H455.091V269.442ZM445.827 269.442H450.459V271.899H445.827V269.442ZM436.562 269.442H441.195V271.899H436.562V269.442ZM427.298 269.442H431.93V271.899H427.298V269.442ZM418.034 269.442H422.666V271.899H418.034V269.442ZM408.77 269.442H413.402V271.899H408.77V269.442ZM399.506 269.442H404.138V271.899H399.506V269.442ZM390.241 269.442H394.874V271.899H390.241V269.442ZM380.977 269.442H385.609V271.899H380.977V269.442ZM371.713 269.442H376.345V271.899H371.713V269.442ZM362.449 269.442H367.081V271.899H362.449V269.442ZM353.185 269.442H357.817V271.899H353.185V269.442ZM343.92 269.442H348.553V271.899H343.92V269.442ZM334.656 269.442H339.288V271.899H334.656V269.442ZM325.392 269.442H330.024V271.899H325.392V269.442ZM316.128 269.442H320.76V271.899H316.128V269.442ZM306.864 269.442H311.496V271.899H306.864V269.442ZM297.599 269.442H302.232V271.899H297.599V269.442ZM288.335 269.442H292.967V271.899H288.335V269.442ZM279.071 269.442H283.703V271.899H279.071V269.442ZM269.807 269.442H274.439V271.899H269.807V269.442ZM262.859 269.442H265.175V271.899H262.859V269.442Z"
              fill="white"
            />
          </g>
          <ellipse
            data-testid="oval-outline"
            cx={OVAL_CX}
            cy={OVAL_CY}
            rx={OVAL_RX}
            ry={OVAL_RY}
            fill="none"
            stroke={RING_WHITE}
            strokeOpacity={RING_OUTLINE_OPACITY}
            strokeWidth={RING_OUTLINE_WIDTH}
          />
          {animation && (
            <OrbitDot
              durationSeconds={durationSeconds}
              color={RING_WHITE}
              running={scanning}
            />
          )}
          {heartMonitor && scanning && <HeartbeatTrace bpm={bpm} color={RING_WHITE} />}
        </svg>
      </MaskSvgWrapper>
    </>
  );
};

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${mirror};
`;

/* Overlay anchored just UNDER the face oval. Holds the alerts (top) and the
   start/retry button (below). Because it's a top-anchored flex column, the first
   item — the button when idle, or the error alert when a scan fails — always
   sits at the same height just below the oval, and when both show the error
   pushes the button down. Tune `top` to sit that first row just under the oval's
   lower edge (the oval bottom is ~78% of the scan area). */
const OvalControls = styled.div`
  position: absolute;
  top: 80%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 7;
  width: 90%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  /* let taps fall through the empty overlay to the video; children opt back in */
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const ButtonSlot = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

/* "Camera is starting" sits ABOVE the button row (which is anchored at
   OvalControls' `top`), on its own so it never pushes the start button down —
   the button stays at a constant height whether or not this message shows. */
const WebcamStartingWrapper = styled.div`
  position: absolute;
  top: 70%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 7;
  width: 90%;
  max-width: 420px;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

const ButtomTimerWrapper = styled(Flex)`
  display: none;
  ${media.tablet`
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    height: 30px;
    display: flex;
  `}
`;

const CenteredTimerWrapper = styled.div`
  position: absolute;
  /* Sits below the ticker band: band top (15% - 60px) + band height (42px) +
   * ~20px gap = 15% + 2px */
  top: calc(15% + 2px);
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 5;
`;

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
`;

const ConfirmBox = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 16px;
  width: min(320px, 88vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  box-sizing: border-box;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
`;

const ConfirmMessage = styled.p`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.normal};
  font-family: inherit;
  color: #222;
  margin: 0;
  line-height: 1.4;
`;

const ConfirmSubMessage = styled.p`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.normal};
  font-family: inherit;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

const ConfirmButton = styled.button<{ variant: "outline" | "solid" }>`
  flex: 1;
  padding: 10px 8px;
  border-radius: 25px;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
  @media (hover: hover) {
    &:hover {
      opacity: 0.85;
    }
  }

  ${({ variant, theme }) =>
    variant === "solid"
      ? `
        background: ${theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
        color: white;
        border: 2px solid ${theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
        @media (hover: hover) { &:hover { background: ${theme.coloreBottoneAttivo ?? buttonDefaults.active}; border-color: ${theme.coloreBottoneAttivo ?? buttonDefaults.active}; } }
      `
      : `
        background: transparent;
        color: ${theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
        border: 2px solid ${theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
        @media (hover: hover) { &:hover { color: ${theme.coloreBottoneAttivo ?? buttonDefaults.active}; border-color: ${theme.coloreBottoneAttivo ?? buttonDefaults.active}; } }
      `}
`;

/* Alerts sit directly ABOVE the button row and grow UPWARD, so an error/stop
   message never pushes the start button down (which would overlap the
   remaining-credits box below it). Taken out of OvalControls' flex flow and
   bottom-anchored to the top edge of the button, with a 12px gap. */
const AlertsWrapper = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const InfoBarWrapper = styled.div`
  height: 0;
  overflow: hidden;
  width: 100%;
  flex-shrink: 0;
`;

const CountdownOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10;
`;

const CountdownNumber = styled.span`
  font-size: 120px;
  font-weight: ${fontWeight.bold};
  color: white;
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
  animation: pulse 1s ease-in-out;

  @keyframes pulse {
    0% {
      transform: scale(0.5);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const GuidanceOverlay = styled.div`
  position: absolute;
  /* Ticker sits above the timer now (they were swapped); band is 42px tall and
   * the timer is ~20px below it — see CenteredTimerWrapper. */
  top: calc(15% - 60px);
  left: 0;
  right: 0;
  z-index: 6;
  overflow: hidden;
  white-space: nowrap;
  background: #fff;
  /* Fixed height (30px text line + ~6px slight padding top/bottom) so the band
   * is its final size the moment it appears. The content is vertically centered,
   * so rolling the ticker in never changes the band height. */
  height: 42px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

/*
 * One-time linear roll-in: the tape starts one band-width off-screen (band empty)
 * and slides in. LINEAR timing (not the old ease-in-out) so there's no
 * slow→fast→slow. Only the wrapper moves during this 12s — the tape's own loop
 * below is delayed until the roll-in finishes and the tape holds still until then
 * — so exactly one thing moves at a time and the on-screen speed is the roll-in
 * speed, then the loop speed. The 12s is tuned so the two speeds are visually
 * equal; exact parity would require measuring band vs. tape width in JS.
 * `both` fill applies the off-screen start on the first frame. RTL rolls in from
 * the opposite edge.
 */
const GuidanceTrack = styled.div<{ $rtl: boolean }>`
  display: block;
  width: 100%;
  animation: ${({ $rtl }) => ($rtl ? "rollInFromLeft" : "rollInFromRight")} 12s
    linear both;

  @keyframes rollInFromRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }
  @keyframes rollInFromLeft {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const GuidanceText = styled.p<{ $rtl: boolean }>`
  margin: 0;
  display: inline-block;
  font-family: "Outfit", sans-serif;
  font-size: 20px;
  font-weight: ${fontWeight.medium};
  line-height: 1.5;
  color: #0d4f7c;
  white-space: nowrap;
  will-change: transform;
  /*
   * Single constant-speed marquee. The content is duplicated (8 identical spans),
   * so translating by exactly 50% of the tape width lands on a visually identical
   * position — a seamless loop. Linear timing keeps the speed uniform from the
   * first frame to the last (no ease, no spin-up), so the text scrolls at one
   * steady pace throughout. 50% over 120s ≈ 0.42%/s.
   * Delayed 12s so it starts only after the GuidanceTrack roll-in finishes; the
   * tape holds at translateX(0) during the delay (its start frame is also 0), so the
   * handoff is seamless and both phases run at a matching constant speed.
   * RTL (Arabic) scrolls the opposite way so it reads right-to-left.
   */
  animation: ${({ $rtl }) =>
    $rtl
      ? "tickerRtl 120s linear 12s infinite"
      : "tickerLtr 120s linear 12s infinite"};

  span::after {
    content: "...";
    padding: 0 1rem;
  }

  @keyframes tickerLtr {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }
  @keyframes tickerRtl {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(50%);
    }
  }
`;

const DevPreviewButton = styled.button`
  display: block;
  margin: 8px auto 0;
  background: none;
  border: 1px dashed #ccc;
  color: #bbb;
  font-size: ${fontSize.xs};
  font-family: "Outfit", sans-serif;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: pointer;
  @media (hover: hover) {
    &:hover {
      color: #888;
      border-color: #aaa;
    }
  }
`;

const RemainingCredits = styled.div`
  width: fit-content;
  margin: 10px auto 0;
  padding: 8px 20px;
  border: 1.5px solid
    ${({ theme }) => theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
  border-radius: ${radius.card};
  background: #ffffff;
  text-align: center;
  font-family: inherit;
`;

const RemainingCreditsLabel = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  color: ${colors.muted};
  line-height: 1.2;
`;

const RemainingCreditsValue = styled.div<{ $zero: boolean }>`
  font-size: ${fontSize.xl3};
  font-weight: ${fontWeight.bold};
  color: ${({ $zero, theme }) =>
    $zero ? '#e53935' : theme.coloreBottoneInattivo ?? buttonDefaults.inactive};
  line-height: 1.2;
`;

interface BiosenseSignalMonitorProps {
  showMonitor: boolean;
  cameraId: string;
  onLicenseStatus: (valid: boolean) => void;
  onSettingsClick: () => void;
  onBack?: () => void;
  isSettingsOpen: boolean;
  onShowReport: (results: MeasurementResults) => void;
  shouldStartMeasurement: boolean;
  onMeasurementStarted: () => void;
  userDemographics: import("./DemographicsForm").UserDemographics | null;
  onPreviewReport?: () => void;
}

const BiosenseSignalMonitor = ({
  showMonitor,
  cameraId,
  onLicenseStatus,
  onSettingsClick,
  onBack,
  isSettingsOpen,
  onShowReport,
  shouldStartMeasurement,
  onMeasurementStarted,
  userDemographics,
  onPreviewReport,
}: BiosenseSignalMonitorProps) => {
  const video = useRef<HTMLVideoElement>(
    null,
  ) as React.MutableRefObject<HTMLVideoElement>;
  const [isMeasurementEnabled, setIsMeasurementEnabled] =
    useState<boolean>(false);
  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingTimeoutRef = useRef<number | undefined>(undefined);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [webcamReady, setWebcamReady] = useState(false);
  const [sessionSuccess, setSessionSuccess] = useState(false);
  const [confirmingBack, setConfirmingBack] = useState(false);
  const [scanAttempted, setScanAttempted] = useState(false);
  const [sessionError, setSessionError] = useState<{
    title: string;
    solution: string;
  } | null>(null);
  const [showGuidance, setShowGuidance] = useState(false);
  const guidanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastWarningRef = useRef<{ title: string; solution: string } | null>(
    null,
  );
  // Generated once per app load — anonymous, not linked to any identity
  const anonymousSessionIdRef = useRef<string>(crypto.randomUUID());
  // Timestamp when the current scan started — used for stopped/failed scans,
  // which have no MeasurementResults (and therefore no results.timestamp/duration).
  const scanStartedAtRef = useRef<number | null>(null);
  // Ref so the MEASURING→ACTIVE effect always reads the latest results, even if
  // the exportResults() state update hasn't re-rendered yet (race condition guard).
  const finalResultsRef = useRef<MeasurementResults | null>(null);
  // True once this scan's outcome has been reported, from whichever path got
  // there first: the MEASURING→ACTIVE effect, the back-confirm handler, or the
  // pagehide beacon. They can overlap — pressing back fires both the handler
  // and pagehide — and each would otherwise write its own row, double-counting
  // one scan against the partner's billed volume. Reset at each scan start.
  const hasReportedOutcomeRef = useRef<boolean>(false);
  // Warning duration tracking
  const warningStartTimeRef = useRef<number | null>(null); // timestamp when current warning began
  const totalWarningMsRef = useRef<number>(0); // accumulated warning ms this scan
  const misdetectionStoppedRef = useRef<boolean>(false); // true if misdetection limit warning was received this scan
  const consecutiveFailuresRef = useRef<number>(0);
  const countdownIntervalRef = useRef<number | null>(null);
  const isPageVisible = usePageVisibility();
  const isMediaTablet = useMediaPredicate("(min-width: 1000px)");
  const { rawToken, branding, voucherCode, crediti, updateCrediti, hideCredits } =
    useContext(BrandingContext);
  // The scan-completion effect below only depends on [sessionState, prevSessionState],
  // so it would otherwise close over a stale rawToken from its first render.
  const rawTokenRef = useRef<string | null>(null);
  rawTokenRef.current = rawToken;

  // A voucher session is any session whose JWT carried a non-empty voucher_code.
  const isVoucherSession = !!voucherCode && voucherCode.trim() !== "";
  // Out of sessions once the live credit count has been driven to 0 (or below).
  const outOfCredits = isVoucherSession && crediti !== null && crediti <= 0;
  const [defaultProcessingTime] = useMeasurementDuration();
  // CE (medical) instances are forced to the full 60 s scan — the clinical
  // readings the CE report shows only resolve at that length. Non-CE instances
  // use the partner-configured duration, then the locally-stored default.
  const processingTime = resolveScanDuration(
    branding.istanza_ce,
    branding.scanDuration,
    defaultProcessingTime,
  );
  const licenseKey = useLicenseKey();
  const intl = useIntl();

  // Block all pointer events during settings open + 400ms after close (ghost-click guard)
  const [blockAfterClose, setBlockAfterClose] = useState(false);
  const prevIsSettingsOpenRef = useRef(false);
  const blockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (prevIsSettingsOpenRef.current && !isSettingsOpen) {
      setBlockAfterClose(true);
      if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
      blockTimerRef.current = setTimeout(() => setBlockAfterClose(false), 400);
    }
    prevIsSettingsOpenRef.current = isSettingsOpen;
    return () => {
      if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
    };
  }, [isSettingsOpen]);

  const { exportResults, downloadLastResults, lastResults } = useResultsExport(
    userDemographics ?? undefined,
    processingTime,
  );

  // Latest license-level capability map. Kept in a ref because useMonitor (its
  // source) is called after useResultsExport, and handleFinalResults must read
  // the current value at scan-end without recreating the callback.
  const enabledVitalSignsRef = useRef<EnabledVitalSigns | null>(null);

  const handleFinalResults = useCallback(
    (vitalSigns: any) => {
      // Store results for display on report page
      const results = exportResults(vitalSigns, false, enabledVitalSignsRef.current ?? undefined);
      // Mirror into a ref so the MEASURING→ACTIVE effect can read it synchronously,
      // before React re-renders with the updated lastResults state value.
      finalResultsRef.current = results ?? null;
    },
    [exportResults],
  );

  const {
    sessionState,
    enabledVitalSigns,
    vitalSigns,
    offlineMeasurements,
    error,
    warning,
    info,
    scanAlertCodesRef,
  } = useMonitor(
    video,
    cameraId,
    processingTime,
    licenseKey,
    null,
    startMeasuring,
    userDemographics ?? undefined,
    handleFinalResults,
    intl.formatMessage,
  );
  // Keep the ref current so handleFinalResults embeds the license map into the
  // exported results at scan-end. The map arrives during license validation,
  // well before MEASURING completes.
  useEffect(() => {
    if (enabledVitalSigns) enabledVitalSignsRef.current = enabledVitalSigns;
  }, [enabledVitalSigns]);

  const prevSessionState = usePrevious(sessionState);
  const { errorMessage, errorSeverity, errorSolution } = useError(
    error,
    intl.formatMessage,
  );
  const { warningMessage, warningSolution, warningSeverity } = useWarning(
    warning,
    intl.formatMessage,
  );

  // Record each face scan session start. The outcome (complete/stopped/failed)
  // is tracked separately below once the scan ends — there is no "started"
  // event on the backend, so we just capture the timestamp here for use in
  // the stopped/failed payload, which has no MeasurementResults of its own.
  useEffect(() => {
    if (
      sessionState === SessionState.MEASURING &&
      prevSessionState !== SessionState.MEASURING
    ) {
      scanStartedAtRef.current = Date.now();
      hasReportedOutcomeRef.current = false;
    }
  }, [sessionState, prevSessionState]);

  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState],
  );

  // visible 1 second after scan starts and turned off when scan completed
  useEffect(() => {
    if (sessionState === SessionState.MEASURING) {
      guidanceTimerRef.current = setTimeout(() => setShowGuidance(true), 1000);
    } else {
      setShowGuidance(false);
    }
    return () => {
      if (guidanceTimerRef.current) {
        clearTimeout(guidanceTimerRef.current);
        guidanceTimerRef.current = null;
      }
    };
  }, [sessionState]);

  const isVideoReady = useCallback(
    () => video.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
    [],
  );

  const startCountdown = useCallback(() => {
    setCountdown(3);
    countdownIntervalRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          // Start the actual measurement when countdown reaches 0
          setStartMeasuring(true);
          loadingTimeoutRef.current = window.setTimeout(
            () => setIsLoading(true),
            processingTime * 1000,
          );
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [processingTime]);

  const cancelCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleButtonClick = useCallback(() => {
    // Voucher session with no credits left: block starting a new scan.
    // (Stopping an in-progress measurement is still allowed to fall through.)
    if (outOfCredits && !isMeasuring()) {
      return;
    }
    setSessionSuccess(false);
    setScanAttempted(false);
    setSessionError(null);

    finalResultsRef.current = null;
    // console.log('Demographics:', demographics);
    // // console.log('License key present:', !!licenseKey);
    // console.log('Video element:', video.current);
    // console.log('Video ready state:', video.current?.readyState);
    // console.log('Video playing:', !video.current?.paused);
    // console.log('Video dimensions:', video.current?.videoWidth, 'x', video.current?.videoHeight);

    // If countdown is active, cancel it
    if (countdown !== null) {
      // console.log('Cancelling countdown...');
      cancelCountdown();
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    if (sessionState === SessionState.ACTIVE) {
      // console.log('Starting countdown...');
      startCountdown();
    } else if (isMeasuring()) {
      // console.log('Stopping measurement...');
      clearTimeout(loadingTimeoutRef.current);
      setStartMeasuring(false);
    } else {
      // console.log('Session not active, current state:', sessionState);
    }
  }, [
    sessionState,
    setIsLoading,
    processingTime,
    userDemographics,
    licenseKey,
    countdown,
    cancelCountdown,
    startCountdown,
    outOfCredits,
    isMeasuring,
  ]);

  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) {
      setIsMeasurementEnabled(false);
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
    // Always reset the spinner when an error arrives so the button is restored
    if (errorMessage) {
      clearTimeout(loadingTimeoutRef.current);
      setIsLoading(false);
    }
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    onLicenseStatus(!(error?.code in HealthMonitorCodes));
  }, [error]);

  const mobile = useMemo(() => isMobile(), []);

  // Determine face border status based on info/error messages during measurement
  const faceBorderStatus = useMemo((): StatusColor => {
    if (!isMeasuring()) return "none";
    if (errorMessage) return "error";
    if (info?.message) return "warning";
    return "success"; // Green when measuring normally
  }, [errorMessage, info?.message, sessionState]);

  // Cleanup countdown interval and loading timeout on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  // Debug export button visibility
  useEffect(() => {
    // console.log('=== EXPORT: Button visibility check:', {
    //   hasLastResults: !!lastResults,
    //   isMeasuringNow: isMeasuring(),
    //   shouldShow: lastResults && !isMeasuring(),
    // });
  }, [lastResults, sessionState]);

  // Track when measurement starts
  useEffect(() => {
    if (
      prevSessionState === SessionState.ACTIVE &&
      sessionState === SessionState.MEASURING
    ) {
      setScanAttempted(true);
      lastWarningRef.current = null;
      finalResultsRef.current = null;
      totalWarningMsRef.current = 0;
      warningStartTimeRef.current = null;
      misdetectionStoppedRef.current = false;

      // Voucher session: charge one credit the moment measurement begins. This
      // mirrors the SDK, which bills the measurement at scan start — so we spend
      // the credit at the same point, regardless of how the scan later ends
      // (completed, failed, stopped, or navigated away from mid-scan). The server
      // response is the source of truth for how many credits remain.
      if (isVoucherSession && rawToken && voucherCode) {
        consumeVoucherCredit(rawToken, voucherCode, 1)
          .then((res) => {
            if (res.success && typeof res.crediti_residui === "number") {
              updateCrediti(res.crediti_residui);
            }
          })
          .catch((err) => {
            // Billing-critical: always surface a failure (even in production
            // builds) so a silently-dropped credit can be diagnosed on-device.
            console.warn("[voucher] Failed to consume credit:", err);
          });
      }
    }
  }, [sessionState, prevSessionState, isVoucherSession, rawToken, voucherCode, updateCrediti]);

  // Keep lastWarningRef in sync during render — updating a ref in render is intentional
  // here and avoids stale-closure / effect-ordering races that plagued the useEffect
  // approach. The ref is read by the MEASURING→ACTIVE effect AFTER this render commits,
  // so it will always see the most recent non-empty message.
  // We only overwrite while measuring so the last value persists after info clears.
  if (sessionState === SessionState.MEASURING) {
    const activeMsg = warningMessage || info.message || null;
    if (activeMsg) {
      lastWarningRef.current = {
        title: activeMsg,
        solution: (warningMessage ? warningSolution : info.solution) ?? "",
      };
      if (
        warning?.code ===
        HealthMonitorCodes.MEASUREMENT_CODE_MISDETECTION_DURATION_EXCEEDS_LIMIT_WARNING
      ) {
        misdetectionStoppedRef.current = true;
      }
      if (!warningStartTimeRef.current) {
        warningStartTimeRef.current = Date.now(); // warning just started
      }
    } else if (warningStartTimeRef.current) {
      totalWarningMsRef.current += Date.now() - warningStartTimeRef.current;
      warningStartTimeRef.current = null; // warning just cleared
    }
  }

  // Reports a stopped/failed scan to the backend. Shared by the natural
  // completion effect below (SDK-driven interruptions — errors, timeouts,
  // misdetection) AND the manual "back mid-scan" confirm handler, which
  // can't rely on that effect: pressing back unmounts this component before
  // any sessionState transition it watches for has a chance to fire, so the
  // report has to be sent explicitly, before navigating away.
  // SDK codes of the scan being reported, omitting the ones never raised.
  const currentScanAlertCodes = (): ScanAlertCodes => {
    const { errorCode, warningCode } = scanAlertCodesRef.current;
    return {
      ...(errorCode !== null ? { errorCode } : {}),
      ...(warningCode !== null ? { warningCode } : {}),
    };
  };

  const reportIncompleteScan = useCallback(
    (sessionResult: "stopped" | "failed", errorPct: number) => {
      if (hasReportedOutcomeRef.current) return;
      hasReportedOutcomeRef.current = true;
      const startedAt = scanStartedAtRef.current ?? Date.now();
      const payload: ScanDataPayload = {
        userID: anonymousSessionIdRef.current,
        sessionGeneratedAt: Math.floor(startedAt / 1000),
        sessionResult,
        sessionTime: Math.round((Date.now() - startedAt) / 1000),
        sessionErrorPercentage: errorPct,
        ...(userDemographics ? mapDemographics(userDemographics) : {}),
        ...currentScanAlertCodes(),
      };
      if (process.env.NODE_ENV === "development") {
        console.log("[Backend payload] Scan did not complete:", payload);
      }
      const token = rawTokenRef.current;
      if (token) {
        // The backend derives the facescan_stopped/failed log row from
        // sessionResult and writes it in the same transaction — see the
        // logEvent flag in postScanData.
        postScanData(token, payload);
      }
    },
    [userDemographics],
  );

  // Reports a scan abandoned by tearing the page down: closing the tab, browser
  // back, locking the phone, or switching apps mid-scan. None of those produce
  // a sessionState transition the completion effect can watch, and none route
  // through the in-app back button, so before this the scan reached neither
  // health_scan_results nor logs — silently, which is why stopped scans read as zero.
  //
  // Scoped to MEASURING on purpose. Loading the app and leaving without ever
  // starting a scan is not a stopped scan, and STOPPING is excluded because
  // results can still arrive from it — reporting there would race the complete
  // path and, on a bfcache restore that resumes, lose the real outcome.
  useEffect(() => {
    if (sessionState !== SessionState.MEASURING) return undefined;

    const handlePageHide = () => {
      if (hasReportedOutcomeRef.current || finalResultsRef.current !== null) {
        return;
      }
      const token = rawTokenRef.current;
      if (!token) return;
      hasReportedOutcomeRef.current = true;

      // Close the warning interval still open at teardown, so the error
      // percentage matches what the normal path would have computed.
      const openWarningMs = warningStartTimeRef.current
        ? Date.now() - warningStartTimeRef.current
        : 0;
      const totalWarningMs = totalWarningMsRef.current + openWarningMs;
      const startedAt = scanStartedAtRef.current ?? Date.now();

      const payload: ScanDataPayload = {
        userID: anonymousSessionIdRef.current,
        sessionGeneratedAt: Math.floor(startedAt / 1000),
        sessionResult: "stopped",
        sessionTime: Math.round((Date.now() - startedAt) / 1000),
        sessionErrorPercentage:
          processingTime > 0
            ? Math.round((totalWarningMs / (processingTime * 1000)) * 100)
            : 0,
        ...(userDemographics ? mapDemographics(userDemographics) : {}),
        ...currentScanAlertCodes(),
      };

      // One beacon, not two: the backend writes both rows in a single
      // transaction, which also keeps this well inside the UA's beacon queue
      // limit at the moment the page is going away.
      postScanDataBeacon(token, payload);
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, [sessionState, processingTime, userDemographics]);

  // Handle all scan-stop outcomes in one place (MEASURING/STOPPING → ACTIVE transition)
  // The SDK lifecycle is MEASURING → STOPPING → ACTIVE. When STOPPING is a distinct
  // React render (e.g. natural 60-second timeout), prevSessionState is STOPPING not
  // MEASURING. Accept both to catch the transition regardless of batching.
  useEffect(() => {
    const comingFromMeasurement =
      prevSessionState === SessionState.MEASURING ||
      prevSessionState === SessionState.STOPPING;
    if (!comingFromMeasurement || sessionState !== SessionState.ACTIVE) return;
    clearTimeout(loadingTimeoutRef.current);
    setIsLoading(false);

    // NB: the voucher credit is charged at scan *start* (see the "measurement
    // just started" effect above), mirroring the SDK's own billing — not here.

    // Close any warning interval still open at the moment the scan ended
    if (warningStartTimeRef.current) {
      totalWarningMsRef.current += Date.now() - warningStartTimeRef.current;
      warningStartTimeRef.current = null;
    }
    const warnSec = (totalWarningMsRef.current / 1000).toFixed(1);
    const pct =
      processingTime > 0
        ? Math.round(
            (totalWarningMsRef.current / (processingTime * 1000)) * 100,
          )
        : 0;
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[Scan quality] Warning time: ${warnSec}s / ${processingTime}s total (${pct}%)`,
        lastWarningRef.current
          ? `— last: "${lastWarningRef.current.title}"`
          : "— no warnings",
      );
    }

    const completedResults = finalResultsRef.current;
    if (completedResults) {
      consecutiveFailuresRef.current = 0;
      // The scan reached a terminal state, so a pagehide firing afterwards
      // must not invent a "stopped" for it.
      hasReportedOutcomeRef.current = true;
      setSessionSuccess(true);

      const token = rawTokenRef.current;
      if (token) {
        // Demographics are required to compute the metric scores, but not to
        // record that the scan completed. Without them we still report the
        // outcome — scoreless — rather than dropping the scan entirely, which
        // previously meant a completed scan went unbilled. Every metric column
        // on the backend is nullable.
        const sessionResult: SessionResult = "complete";
        const sessionPayload: ScanDataPayload = userDemographics
          ? buildBackendPayload(
              anonymousSessionIdRef.current,
              completedResults,
              userDemographics,
              sessionResult,
              pct,
            )
          : {
              userID: anonymousSessionIdRef.current,
              sessionGeneratedAt: Math.floor(
                (scanStartedAtRef.current ?? Date.now()) / 1000,
              ),
              sessionResult,
              sessionTime: processingTime,
              sessionErrorPercentage: pct,
            };
        // CE instances also send their clinical readings (no demographics
        // needed) — see withCeClinicalFields.
        const payload = withCeClinicalFields(
          sessionPayload,
          completedResults,
          branding.istanza_ce,
        );
        if (process.env.NODE_ENV === "development") {
          console.log("[Backend payload]", payload);
        }
        // Single request: the backend writes the logs row in the same
        // transaction (see the logEvent flag in postScanData), so there is no
        // second call to sequence behind this one.
        postScanData(token, payload);
      }

      // A successful scan goes straight to the report page — regardless of the
      // warning percentage — so there is no intermediate "Continue" button.
      onShowReport(completedResults);
    } else {
      // No results delivered → scan was interrupted or failed
      let title = errorMessage || lastWarningRef.current?.title || "";
      let solution = errorSolution || lastWarningRef.current?.solution || "";
      if (misdetectionStoppedRef.current) {
        title = intl.formatMessage({ id: "monitor.misdetectionStopped" });
        solution = intl.formatMessage({
          id: "monitor.misdetectionStopped.solution",
        });
      }
      if (!title) {
        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current >= 2) {
          title = intl.formatMessage({ id: "monitor.pleaseRefresh" });
        } else {
          title = intl.formatMessage({ id: "monitor.scanIncomplete" });
        }
      } else {
        consecutiveFailuresRef.current = 0;
      }
      setSessionError({ title, solution });

      // Report to the backend — fires even without demographics (optional
      // there); userDemographics may be null if the user aborted before
      // entering them. No MeasurementResults exist here, so timing comes
      // from scanStartedAtRef rather than results.timestamp/sessionInfo.duration.
      if (finalResultsRef.current === null) {
        reportIncompleteScan(errorMessage ? "failed" : "stopped", pct);
      }
    }
  }, [sessionState, prevSessionState, reportIncompleteScan]);

  // Auto-start measurement when triggered from "Measure Again" button.
  // Only fires on the false→true transition of shouldStartMeasurement to avoid
  // spurious re-triggers when other effect deps (e.g. onMeasurementStarted) change.
  const prevShouldStartMeasurement = usePrevious(shouldStartMeasurement);
  useEffect(() => {
    if (
      shouldStartMeasurement &&
      !prevShouldStartMeasurement &&
      sessionState === SessionState.ACTIVE
    ) {
      handleButtonClick();
      if (onMeasurementStarted) {
        onMeasurementStarted();
      }
    }
  }, [
    shouldStartMeasurement,
    prevShouldStartMeasurement,
    sessionState,
    handleButtonClick,
    onMeasurementStarted,
  ]);

  const handleBack = useCallback(() => {
    if (isMeasuring()) {
      setConfirmingBack(true);
    } else {
      onBack?.();
    }
  }, [isMeasuring, onBack]);

  if (!showMonitor) return null;

  return (
    <MonitorOuterWrapper isBlocked={isSettingsOpen || blockAfterClose}>
      <TopBar onBack={onBack ? handleBack : undefined} />
      <MonitorWrapper isSettingsOpen={isSettingsOpen}>
        <MeasurementContentWrapper>
          <InfoBarWrapper>
            <InfoBar
              showTimer={false}
              isMeasuring={isMeasuring()}
              durationSeconds={processingTime}
              offlineMeasurements={offlineMeasurements}
            />
          </InfoBarWrapper>
          <VideoAndStatsWrapper>
            <VideoWrapper>
              <DynamicMask
                status={faceBorderStatus}
                color={branding.coloreBottoneAttivo ?? buttonDefaults.active}
                durationSeconds={processingTime}
                scanning={isMeasurementEnabled && isMeasuring()}
                bpm={vitalSigns?.pulseRate?.value}
                video={video}
                animation={branding.facescanAnimation}
                heartMonitor={branding.facescanHeartMonitor}
              />
              <Video
                ref={video}
                id="video"
                muted={true}
                playsInline={true}
                onCanPlay={() => setWebcamReady(true)}
              />
            </VideoWrapper>
            {branding.facescanAnimatedText &&
              isMeasurementEnabled &&
              isMeasuring() && (
              <GuidanceOverlay>
                {showGuidance && isMeasuring() &&
                  (() => {
                    const guidance = intl.formatMessage({
                      id: "monitor.scanGuidance",
                    });
                    // Render the message twice so the -50%→0 loop is seamless;
                    // each half repeats the text a few times to keep the tape
                    // populated.
                    return (
                      <GuidanceTrack $rtl={intl.locale === "ar"}>
                        <GuidanceText $rtl={intl.locale === "ar"}>
                          {Array.from({ length: 8 }).map((_, i) => (
                            <span key={i}>{guidance}</span>
                          ))}
                        </GuidanceText>
                      </GuidanceTrack>
                    );
                  })()}
              </GuidanceOverlay>
            )}
            {countdown !== null && (
              <CountdownOverlay>
                <CountdownNumber key={countdown}>{countdown}</CountdownNumber>
              </CountdownOverlay>
            )}
            {isMeasurementEnabled && isMeasuring() && (
              <CenteredTimerWrapper>
                <Timer
                  started={isMeasuring()}
                  durationSeconds={processingTime}
                />
              </CenteredTimerWrapper>
            )}
            {(isMeasuring()
              ? !errorMessage && !warningMessage
              : !errorMessage) && isMeasurementEnabled}
            {!isVideoReady() && licenseKey && <Loader />}
            {confirmingBack && (
              <ConfirmOverlay>
                <ConfirmBox>
                  <ConfirmMessage>
                    {intl.formatMessage({ id: "monitor.backConfirm.message" })}
                  </ConfirmMessage>
                  {isVoucherSession && (
                    <ConfirmSubMessage>
                      {intl.formatMessage({ id: "monitor.backConfirm.creditNote" })}
                    </ConfirmSubMessage>
                  )}
                  <ConfirmButtons>
                    <ConfirmButton
                      variant="outline"
                      onClick={() => setConfirmingBack(false)}
                    >
                      {intl.formatMessage({ id: "monitor.backConfirm.cancel" })}
                    </ConfirmButton>
                    <ConfirmButton
                      variant="solid"
                      onClick={() => {
                        setConfirmingBack(false);
                        // onBack unmounts this component, so the natural
                        // completion effect never gets a live render to react
                        // to a sessionState change — report explicitly first.
                        if (finalResultsRef.current === null) {
                          reportIncompleteScan("stopped", 0);
                        }
                        onBack?.();
                      }}
                    >
                      {intl.formatMessage({ id: "monitor.backConfirm.ok" })}
                    </ConfirmButton>
                  </ConfirmButtons>
                </ConfirmBox>
              </ConfirmOverlay>
            )}
          </VideoAndStatsWrapper>
          {!webcamReady && !errorMessage && (
            <WebcamStartingWrapper>
              <InfoAlert
                message={intl.formatMessage({ id: "monitor.webcamStarting" })}
              />
            </WebcamStartingWrapper>
          )}
          <OvalControls>
            {/* Once a scan is meant to start (the 3-2-1 countdown is running),
                suppress every error/warning/info alert. The SDK-derived
                errorMessage (and any lingering warning) persists past a restart
                until measurement actually begins, so without this gate a stale
                error from the previous attempt stays on screen during the
                countdown. */}
            <AlertsWrapper>
              {countdown === null && (
                <>
                  <ErrorAlert
                    message={errorMessage}
                    severity={errorSeverity}
                    solution={errorSolution}
                  />
                  {isMeasuring() && (
                    <WarningAlert
                      message={warningMessage}
                      solution={warningSolution}
                      severity={warningSeverity}
                    />
                  )}
                  {isMeasuring() && (
                    <InfoAlert message={info.message} solution={info.solution} />
                  )}
                  {!isMeasuring() && sessionError && !errorMessage && (
                    <InfoAlert
                      message={sessionError.title}
                      solution={sessionError.solution}
                    />
                  )}
                  {/* `!sessionSuccess` avoids a flash: charging at scan start can
                      drop the balance to 0 during a successful scan, and this alert
                      would otherwise blink for the single frame between the scan
                      ending and onShowReport navigating to the report. A failed/
                      idle 0-credit state (sessionSuccess false) still shows it. */}
                  {!isMeasuring() && outOfCredits && !sessionSuccess && (
                    <InfoAlert
                      message={intl.formatMessage({ id: "monitor.outOfCredits" })}
                    />
                  )}
                </>
              )}
            </AlertsWrapper>
            <ButtonSlot>
              {/* Out of credits (and not mid-scan): hide the start button
                  entirely rather than showing it greyed-out — there's no
                  action left to take until a new voucher session begins. */}
              {!(outOfCredits && !isMeasuring()) && (
                <StartButton
                  isLoading={isLoading}
                  isMeasuring={isMeasuring()}
                  hasError={scanAttempted && !sessionSuccess && !isMeasuring()}
                  disabled={!webcamReady}
                  onClick={handleButtonClick}
                />
              )}
            </ButtonSlot>
          </OvalControls>
        </MeasurementContentWrapper>
      </MonitorWrapper>

      {/* `hide_credits: true` in the JWT turns the counter off for partners who
          don't want the balance on screen. The session is still metered: the
          out-of-credits alert and the hidden start button above are driven by
          `outOfCredits`, not by this flag. */}
      {isVoucherSession && crediti !== null && !hideCredits && (
        <RemainingCredits>
          <RemainingCreditsLabel>
            {intl.formatMessage({ id: "monitor.remainingCredits" })}
          </RemainingCreditsLabel>
          <RemainingCreditsValue $zero={crediti === 0}>{crediti}</RemainingCreditsValue>
        </RemainingCredits>
      )}
      {onPreviewReport && process.env.NODE_ENV === "development" && (
        <DevPreviewButton onClick={onPreviewReport}>
          [dev] preview report
        </DevPreviewButton>
      )}
      <FooterLogo />
    </MonitorOuterWrapper>
  );
};

export default BiosenseSignalMonitor;
